import React, { useState, useEffect } from "react";
import API_BASE_URL from "../../../config/api";
import { utils, writeFile } from "xlsx";
import AOS from "aos";
import "aos/dist/aos.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaDownload, FaExclamationTriangle, FaFolderOpen, FaUserCircle, FaExternalLinkAlt, FaGithub, FaCheck, FaTimes, FaPencilAlt, FaPlus, FaFileAlt, FaTrash, FaCalendarAlt, FaSave } from "react-icons/fa";

function ProgressContainer() {
    const navigate = useNavigate();
    const { user, activeRole, isAuthenticated } = useAuth();
    const [currentTable, setCurrentTable] = useState(1);
    const [data, setData] = useState([
        [
            { period: "Jan. First fortnight", task: "", comment: "", signature: "" },
            { period: "Jan. Second fortnight", task: "", comment: "", signature: "" },
            { period: "Feb. First fortnight", task: "", comment: "", signature: "" },
            { period: "Feb. Second fortnight", task: "", comment: "", signature: "" },
            { period: "March First fortnight", task: "", comment: "", signature: "" },
            { period: "March Second fortnight", task: "", comment: "", signature: "" },
            { period: "April First fortnight", task: "", comment: "", signature: "" },
            { period: "April Second fortnight", task: "", comment: "", signature: "" },
            { period: "May First fortnight", task: "", comment: "", signature: "" },
            { period: "May Second fortnight", task: "", comment: "", signature: "" },
        ],
        [
            { period: "June First fortnight", task: "", comment: "", signature: "" },
            { period: "June Second fortnight", task: "", comment: "", signature: "" },
            { period: "July First fortnight", task: "", comment: "", signature: "" },
            { period: "July Second fortnight", task: "", comment: "", signature: "" },
            { period: "August First fortnight", task: "", comment: "", signature: "" },
            { period: "August Second fortnight", task: "", comment: "", signature: "" },
            { period: "Sept. First fortnight", task: "", comment: "", signature: "" },
            { period: "Sept. Second fortnight", task: "", comment: "", signature: "" },
            { period: "Oct. First fortnight", task: "", comment: "", signature: "" },
            { period: "Oct. Second fortnight", task: "", comment: "", signature: "" },
            { period: "Nov. First fortnight", task: "", comment: "", signature: "" },
        ],
    ]);

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resources, setResources] = useState({
        githubLink: "",
        resources: "",
    });
    const [newGithubLink, setNewGithubLink] = useState("");
    const [isEditingRepo, setIsEditingRepo] = useState(false);
    const [githubLinkError, setGithubLinkError] = useState("");
    const [projectId, setProjectId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Resources state
    const [newResourcesLink, setNewResourcesLink] = useState("");
    const [isEditingResources, setIsEditingResources] = useState(false);
    const [resourcesLinkError, setResourcesLinkError] = useState("");

    useEffect(() => {
        AOS.init({ duration: 1000 });

        const fetchProjects = async () => {
            try {
                if (!isAuthenticated || !user) {
                    console.error("User not authenticated");
                    navigate("/");
                    return;
                }

                const userEmail = user.email;

                if (!userEmail) {
                    console.error("No email found for user:", user);
                    toast.error("User email not found. Please contact support.");
                    setLoading(false);
                    return;
                }

                // console.log("Fetching projects for student with email:", userEmail);

                const response = await axios.get(
                    `${API_BASE_URL}/api/projects/student/email/${encodeURIComponent(userEmail)}`
                );

                if (response.status !== 200) {
                    throw new Error("Failed to fetch projects");
                }

                const data = response.data;
                // console.log("Projects response:", data);

                if (!data.success) {
                    throw new Error(data.message || "Failed to fetch projects");
                }

                const guideAssignedProjects = data.data.filter(
                    (project) => project.status === "guide-assigned"
                );

                if (guideAssignedProjects.length > 0) {
                    setProjects(guideAssignedProjects);
                    setProjectId(guideAssignedProjects[0]._id);
                    fetchProgressData(guideAssignedProjects[0]._id);
                } else {
                    setError(
                        activeRole === "student" || activeRole === "sa"
                            ? "No projects with assigned guides found. Please wait for guide assignment."
                            : "No assigned projects"
                    );
                    setLoading(false);
                }
            } catch (err) {
                console.error("Error in fetchProjects:", err);
                setError(err.message);
                setLoading(false);

                if (err.response?.status === 401) {
                    toast.error("Session expired. Please login again.");
                    navigate("/");
                }
            }
        };

        if (isAuthenticated) {
            fetchProjects();
        } else {
            setLoading(false);
        }
    }, [isAuthenticated, user, navigate, activeRole]);

    const fetchProgressData = async (id) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/progress/${id}`
            );

            if (response.status !== 200) {
                throw new Error("Failed to fetch progress data");
            }

            const result = response.data;
            if (result.success && result.data) {
                const progressData = result.data;
                const sem6Data =
                    progressData.semesters.find((s) => s.semester === 6)
                        ?.entries || data[0];
                const sem7Data =
                    progressData.semesters.find((s) => s.semester === 7)
                        ?.entries || data[1];
                setData([sem6Data, sem7Data]);

                setResources({
                    githubLink: progressData.githubLink || "",
                    resources: progressData.resources || "",
                });

                if (result.project) {
                    setProjects((prev) => {
                        const updatedProjects = [...prev];
                        updatedProjects[0] = {
                            ...updatedProjects[0],
                            teamMembers: result.project.teamMembers,
                            objectives: result.project.objectives,
                        };
                        return updatedProjects;
                    });
                }
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching progress data:", err);
            setLoading(false);

            if (err.response?.status === 401) {
                toast.error("Session expired. Please login again.");
                navigate("/");
            }
        }
    };

    const handleInputChange = (tableIndex, rowIndex, field, value) => {
        const newData = [...data];
        newData[tableIndex][rowIndex][field] = value;
        setData(newData);
    };

    const handleSaveProgress = () => {
        const semester = currentTable === 1 ? 6 : 7;
        const entries = data[currentTable - 1];
        saveFortnightEntries(semester, entries);
    };

    const saveFortnightEntries = async (semester, entries) => {
        if (!projectId) return;
        try {
            setIsSaving(true);
            const response = await axios.put(
                `${API_BASE_URL}/api/progress/${projectId}/entries`,
                {
                    semester,
                    entries,
                }
            );

            if (response.status !== 200) {
                throw new Error("Failed to update progress entries");
            }
            toast.success("Progress saved", { autoClose: 2000 });
        } catch (err) {
            console.error("Error saving progress data:", err);
            toast.error("Failed to save progress");

            if (err.response?.status === 401) {
                toast.error("Session expired. Please login again.");
                navigate("/");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownloadReport = () => {
        try {
            const wb = utils.book_new();
            const sem6Headers = [
                ["Fortnight Period", "Task", "Comment", "Signature"],
            ];
            const sem6Data = data[0].map((row) => [
                row.period,
                row.task,
                row.comment,
                row.signature,
            ]);
            const sem6Worksheet = utils.aoa_to_sheet([
                ...sem6Headers,
                ...sem6Data,
            ]);
            const sem7Headers = [
                ["Fortnight Period", "Task", "Comment", "Signature"],
            ];
            const sem7Data = data[1].map((row) => [
                row.period,
                row.task,
                row.comment,
                row.signature,
            ]);
            const sem7Worksheet = utils.aoa_to_sheet([
                ...sem7Headers,
                ...sem7Data,
            ]);
            utils.book_append_sheet(wb, sem6Worksheet, "Semester 6");
            utils.book_append_sheet(wb, sem7Worksheet, "Semester 7");
            writeFile(
                wb,
                `Project_Progress_Report_${new Date().toISOString().split("T")[0]
                }.xlsx`
            );
            toast.success("Report downloaded successfully");
        } catch (err) {
            console.error("Error generating report:", err);
            toast.error("Failed to download report");
        }
    };

    // Function to validate GitHub link format
    const isValidGithubLink = (link) => {
        const githubRegex = /^https:\/\/github\.com\/.+/i;
        return githubRegex.test(link);
    };

    const handleGithubLinkChange = (e) => {
        setNewGithubLink(e.target.value);
        if (githubLinkError) {
            setGithubLinkError("");
        }
    };

    const saveGithubLink = async () => {
        if (!newGithubLink.trim()) {
            setGithubLinkError("GitHub link cannot be empty");
            return;
        }

        if (!isValidGithubLink(newGithubLink.trim())) {
            setGithubLinkError("Invalid GitHub link");
            return;
        }

        if (newGithubLink.trim() && projectId) {
            try {
                const response = await axios.put(
                    `${API_BASE_URL}/api/progress/${projectId}/github`,
                    {
                        githubLink: newGithubLink.trim(),
                    }
                );

                if (response.status !== 200) {
                    throw new Error("Failed to update GitHub link");
                }

                setResources({
                    ...resources,
                    githubLink: newGithubLink.trim(),
                });
                setIsEditingRepo(false);
                setGithubLinkError("");
                toast.success("GitHub link updated successfully");
            } catch (err) {
                console.error("Error updating GitHub link:", err);
                toast.error("Failed to update GitHub link");

                if (err.response?.status === 401) {
                    toast.error("Session expired. Please login again.");
                    navigate("/");
                }
            }
        }
    };

    const isValidGoogleDriveLink = (link) => {
        const driveRegex = /^https:\/\/drive\.google\.com\/drive\/folders\/.*?\?usp=sharing$/i;
        return driveRegex.test(link);
    };

    const handleResourcesLinkChange = (e) => {
        setNewResourcesLink(e.target.value);
        if (resourcesLinkError) {
            setResourcesLinkError("");
        }
    };

    const saveResourcesLink = async () => {
        if (!newResourcesLink.trim()) {
            setResourcesLinkError("Resources link cannot be empty");
            return;
        }

        if (!isValidGoogleDriveLink(newResourcesLink.trim())) {
            setResourcesLinkError("Please provide a valid Google Drive link");
            return;
        }

        if (projectId) {
            try {
                const response = await axios.put(
                    `${API_BASE_URL}/api/progress/${projectId}/resources`,
                    {
                        resources: newResourcesLink.trim(),
                    }
                );

                if (response.status !== 200) {
                    throw new Error("Failed to update resources link");
                }

                setResources({
                    ...resources,
                    resources: newResourcesLink.trim(),
                });
                setIsEditingResources(false);
                setResourcesLinkError("");
                toast.success("Resources link updated successfully");
            } catch (err) {
                console.error("Error updating resources link:", err);
                toast.error("Failed to update resources link");

                if (err.response?.status === 401) {
                    toast.error("Session expired. Please login again.");
                    navigate("/");
                }
            }
        }
    };

    const viewProjectDetails = () => {
        if (projects.length > 0 && projects[0]._id) {
            navigate(`/project/${projects[0]._id}`);
        } else {
            toast.error("Cannot access project details at this time");
        }
    };

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            <div
                className="flex flex-col md:flex-row justify-between items-center bg-[#B7202E] rounded-xl p-6 md:p-8 text-white shadow-md mb-8 gap-4"
                data-aos="fade-down"
            >
                <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2 text-shadow-sm">Progress Review</h1>
                    <p className="text-sm opacity-80 font-medium">
                        {new Date().toLocaleDateString("en-IN", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}
                    </p>
                </div>
                <div className="flex gap-4">
                    <button
                        className="bg-white/15 hover:bg-white/25 text-white border border-white/30 px-5 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all shadow-sm hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
                        onClick={handleDownloadReport}
                    >
                        <FaDownload /> Download Report
                    </button>
                </div>
            </div>

            <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800 relative pb-3 after:absolute after:bottom-0 after:left-0 after:w-16 after:h-1 after:bg-[#B7202E] after:rounded-full">Project Overview</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {loading ? (
                        <div className="col-span-full bg-white rounded-xl p-12 text-center text-gray-500 shadow-sm animate-pulse">
                            <div className="inline-block w-8 h-8 border-4 border-[#b7202e] border-t-transparent rounded-full animate-spin mb-4"></div>
                            <div>Loading project details...</div>
                        </div>
                    ) : error ? (
                        <div className="col-span-full bg-white rounded-xl p-8 border border-red-200 text-center shadow-sm">
                            <div className="text-red-500 text-4xl mb-4 flex justify-center"><FaExclamationTriangle /></div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Project Not Approved yet</h3>
                            <p className="text-gray-600">{error}</p>
                        </div>
                    ) : projects.length > 0 ? (
                        <>
                            <div className="col-span-1 md:col-span-2 bg-white rounded-2xl p-6 shadow-sm border-t-4 border-[#B7202E] flex flex-col min-h-[220px]">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
                                    <span className="bg-[#B7202E]/10 p-2 rounded-lg text-[#B7202E] flex items-center justify-center w-9 h-9">
                                        <FaFolderOpen className="text-base" />
                                    </span>
                                    Project Details
                                </h3>
                                <div className="mt-2 flex-grow">
                                    <h4 className="text-xl font-bold text-[#B7202E] mb-4 leading-tight">
                                        {projects[0].projectTitle}
                                    </h4>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Category</span>
                                            <span className="text-gray-800 font-medium break-words">
                                                {Object.entries(projects[0].category)
                                                    .filter(([_, value]) => value === true)
                                                    .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
                                                    .join(", ")}
                                            </span>
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Status</span>
                                            <div>
                                                <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white tracking-wide ${projects[0].status === 'guide-assigned' ? 'bg-purple-600' : 'bg-gray-500'}`}>
                                                    {projects[0].status.toUpperCase()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>


                                    <div className="flex flex-col gap-1 mb-2">
                                        <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Created Date</span>
                                        <span className="text-gray-800 font-medium">
                                            {new Date(projects[0].date).toLocaleDateString("en-IN", {
                                                day: "numeric",
                                                month: "long",
                                                year: "numeric",
                                            })}
                                        </span>
                                    </div>
                                </div>

                                {projects[0].teamMembers && projects[0].teamMembers.length > 0 && (
                                    <div className="bg-gray-50 p-4 rounded-xl mb-4 border border-gray-100">
                                        <h5 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">Team Members</h5>
                                        <ul className="flex flex-wrap gap-2">
                                            {projects[0].teamMembers.map((member, index) => (
                                                <li key={index} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-xs font-medium text-gray-700 shadow-sm border border-gray-200">
                                                    <FaUserCircle className="text-[#B7202E] text-sm" /> {member}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div>
                                    <button
                                        className="inline-flex items-center gap-2 bg-[#B7202E] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#961925] transition-all transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                                        onClick={viewProjectDetails}
                                    >
                                        <FaExternalLinkAlt /> View Full Details
                                    </button>
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-md h-full">
                                <div className="absolute top-0 left-0 w-full h-1 bg-[#B7202E]"></div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
                                    <span className="bg-[#B7202E]/10 p-2 rounded-lg text-[#B7202E] flex items-center justify-center w-9 h-9">
                                        <FaGithub className="text-base" />
                                    </span>
                                    GitHub Repository
                                </h3>

                                {isEditingRepo ? (
                                    <div className="flex flex-col gap-3 flex-grow">
                                        <div>
                                            <input
                                                type="text"
                                                value={newGithubLink}
                                                onChange={handleGithubLinkChange}
                                                placeholder="https://github.com/username/repo"
                                                className={`w-full p-2.5 rounded-lg border text-sm ${githubLinkError ? "border-red-300 bg-red-50 focus:ring-red-200" : "border-gray-200 focus:border-[#B7202E] focus:ring-2 focus:ring-[#B7202E]/10"} outline-none transition-all`}
                                            />
                                            {githubLinkError && (
                                                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                    <FaExclamationTriangle className="text-[10px]" /> {githubLinkError}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 mt-auto pt-2">
                                            <button
                                                className="flex-1 bg-[#B7202E] text-white py-2 rounded-lg text-xs font-semibold hover:bg-[#961925] transition-colors flex items-center justify-center gap-1"
                                                onClick={saveGithubLink}
                                            >
                                                <FaCheck /> Save
                                            </button>
                                            <button
                                                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                                                onClick={() => {
                                                    setIsEditingRepo(false);
                                                    setGithubLinkError("");
                                                }}
                                            >
                                                <FaTimes /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col flex-grow">
                                        <p className="text-sm text-gray-600 mb-4 flex-grow">
                                            Access your project's source code and track changes.
                                        </p>
                                        {resources.githubLink ? (
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mt-auto">
                                                <div className="flex justify-between items-center mb-2">
                                                    <a
                                                        href={resources.githubLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[#B7202E] font-medium text-sm hover:underline flex items-center gap-1 truncate max-w-[150px]"
                                                    >
                                                        <FaExternalLinkAlt className="text-xs" /> Open Repo
                                                    </a>
                                                    <button
                                                        className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 text-gray-500 hover:text-[#B7202E] hover:border-[#B7202E] transition-all"
                                                        onClick={() => {
                                                            setNewGithubLink(resources.githubLink);
                                                            setIsEditingRepo(true);
                                                        }}
                                                    >
                                                        <FaPencilAlt className="text-xs" />
                                                    </button>
                                                </div>
                                                <div className="text-xs text-gray-400 truncate">
                                                    {resources.githubLink.replace('https://github.com/', '')}
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                className="w-full mt-auto bg-[#B7202E] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#961925] transition-colors flex items-center justify-center gap-2 shadow-sm"
                                                onClick={() => setIsEditingRepo(true)}
                                            >
                                                <FaPlus /> Add Repository
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-md h-full">
                                <div className="absolute top-0 left-0 w-full h-1 bg-[#B7202E]"></div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
                                    <span className="bg-[#B7202E]/10 p-2 rounded-lg text-[#B7202E] flex items-center justify-center w-9 h-9">
                                        <FaFileAlt className="text-base" />
                                    </span>
                                    Resources (Google Drive)
                                </h3>

                                {isEditingResources ? (
                                    <div className="flex flex-col gap-3 flex-grow">
                                        <div>
                                            <input
                                                type="text"
                                                value={newResourcesLink}
                                                onChange={handleResourcesLinkChange}
                                                placeholder="https://drive.google.com/..."
                                                className={`w-full p-2.5 rounded-lg border text-sm ${resourcesLinkError ? "border-red-300 bg-red-50 focus:ring-red-200" : "border-gray-200 focus:border-[#B7202E] focus:ring-2 focus:ring-[#B7202E]/10"} outline-none transition-all`}
                                            />
                                            {resourcesLinkError && (
                                                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                    <FaExclamationTriangle className="text-[10px]" /> {resourcesLinkError}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 mt-auto pt-2">
                                            <button
                                                className="flex-1 bg-[#B7202E] text-white py-2 rounded-lg text-xs font-semibold hover:bg-[#961925] transition-colors flex items-center justify-center gap-1"
                                                onClick={saveResourcesLink}
                                            >
                                                <FaCheck /> Save
                                            </button>
                                            <button
                                                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                                                onClick={() => {
                                                    setIsEditingResources(false);
                                                    setResourcesLinkError("");
                                                }}
                                            >
                                                <FaTimes /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col flex-grow">
                                        <p className="text-sm text-gray-600 mb-4 flex-grow">
                                            Upload your project resources to Google Drive and share the link here.
                                            Like meeting online recordings, Offline meets with guide, reports etc
                                        </p>
                                        {resources.resources ? (
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mt-auto">
                                                <div className="flex justify-between items-center mb-2">
                                                    <a
                                                        href={resources.resources}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[#B7202E] font-medium text-sm hover:underline flex items-center gap-1 truncate max-w-[150px]"
                                                    >
                                                        <FaExternalLinkAlt className="text-xs" /> Open Resources
                                                    </a>
                                                    <button
                                                        className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 text-gray-500 hover:text-[#B7202E] hover:border-[#B7202E] transition-all"
                                                        onClick={() => {
                                                            setNewResourcesLink(resources.resources);
                                                            setIsEditingResources(true);
                                                        }}
                                                    >
                                                        <FaPencilAlt className="text-xs" />
                                                    </button>
                                                </div>
                                                <div className="text-xs text-gray-400 truncate">
                                                    {resources.resources}
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                className="w-full mt-auto bg-[#B7202E] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#961925] transition-colors flex items-center justify-center gap-2 shadow-sm"
                                                onClick={() => setIsEditingResources(true)}
                                            >
                                                <FaPlus /> Add Resources
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm mb-8 border border-gray-100 md:col-span-2 col-span-1 lg:col-span-4">
                                <h2 className="text-2xl font-semibold mb-6 text-gray-800 relative pb-3 after:absolute after:bottom-0 after:left-0 after:w-16 after:h-1 after:bg-[#B7202E] after:rounded-full">
                                    Fortnight Progress Checks
                                </h2>

                                <div className="flex flex-wrap gap-4 mb-8">
                                    <button
                                        className={`px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all duration-300 ${currentTable === 1 ? "bg-[#B7202E] text-white shadow-md transform scale-105" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                                        onClick={() => setCurrentTable(1)}
                                    >
                                        <FaCalendarAlt /> Semester 6
                                    </button>
                                    <button
                                        className={`px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all duration-300 ${currentTable === 2 ? "bg-[#B7202E] text-white shadow-md transform scale-105" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                                        onClick={() => setCurrentTable(2)}
                                    >
                                        <FaCalendarAlt /> Semester 7
                                    </button>
                                </div>

                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                    <div className="p-6 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                                        <h3 className="font-bold text-gray-700">Timeline Chart for UG Project Work Completion</h3>
                                        <div className="flex items-center gap-2">
                                            {isSaving && (
                                                <span className="text-xs font-semibold bg-[#B7202E] text-white px-3 py-1 rounded-full animate-pulse flex items-center gap-1">
                                                    <FaSave className="text-xs" /> Saving...
                                                </span>
                                            )}
                                            <button
                                                className="bg-[#B7202E] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#961925] transition-colors flex items-center gap-2 shadow-sm"
                                                onClick={handleSaveProgress}
                                                disabled={isSaving}
                                            >
                                                <FaSave /> Save Progress
                                            </button>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-[#B7202E] text-white">
                                                    <th className="p-4 font-semibold text-sm w-1/4 min-w-[200px]">Fortnight Period</th>
                                                    <th className="p-4 font-semibold text-sm w-1/3 min-w-[250px]">Task (to be filled by project group)</th>
                                                    <th className="p-4 font-semibold text-sm min-w-[250px]">Guide's Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {data[currentTable - 1].map((row, index) => (
                                                    <tr key={index} className="hover:bg-red-50/30 transition-colors">
                                                        <td className="p-4 text-sm font-medium text-[#B7202E] border-r border-gray-100 bg-gray-50/50">
                                                            {row.period}
                                                        </td>
                                                        {activeRole === "student" || activeRole === "sa" ? (
                                                            <>
                                                                <td className="p-4 border-r border-gray-100">
                                                                    <input
                                                                        type="text"
                                                                        value={row.task}
                                                                        placeholder="Enter your task here..."
                                                                        onChange={(e) => handleInputChange(currentTable - 1, index, "task", e.target.value)}
                                                                        className="w-full p-2.5 rounded-lg border border-gray-300 text-sm focus:border-[#B7202E] focus:ring-2 focus:ring-[#B7202E]/10 focus:outline-none transition-all bg-white"
                                                                    />
                                                                </td>
                                                                <td className="p-4 text-sm text-gray-600">
                                                                    {row.comment ? (
                                                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 italic text-gray-700">
                                                                            "{row.comment}"
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-gray-400 italic">No comments yet</span>
                                                                    )}
                                                                </td>
                                                            </>
                                                        ) : activeRole === "guide" ? (
                                                            <>
                                                                <td className="p-4 text-sm text-gray-700 border-r border-gray-100">
                                                                    {row.task ? row.task : <span className="text-gray-400 italic">No task entered</span>}
                                                                </td>
                                                                <td className="p-4">
                                                                    <input
                                                                        type="text"
                                                                        value={row.comment}
                                                                        placeholder="Enter your comment here..."
                                                                        onChange={(e) => handleInputChange(currentTable - 1, index, "comment", e.target.value)}
                                                                        className="w-full p-2.5 rounded-lg border border-gray-300 text-sm focus:border-[#B7202E] focus:ring-2 focus:ring-[#B7202E]/10 focus:outline-none transition-all bg-white"
                                                                    />
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td className="p-4 text-sm text-gray-600 border-r border-gray-100">
                                                                    {row.task || <span className="text-gray-400 italic">No task entered</span>}
                                                                </td>
                                                                <td className="p-4 text-sm text-gray-600">
                                                                    {row.comment || <span className="text-gray-400 italic">No comments</span>}
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="col-span-full bg-yellow-50 rounded-xl p-8 border border-yellow-200 text-center">
                            <h3 className="text-lg font-bold text-yellow-700 mb-2">No Project Found</h3>
                            <p className="text-yellow-600">No approved projects available.</p>
                        </div>
                    )}


                </div>
            </div>


        </div >
    );
}

export default ProgressContainer;
